// Third Party Imports
import {createStore as reduxCreateStore, applyMiddleware, compose, combineReducers} from 'redux';
import {Map} from 'immutable';

// Artifactory
import {middlewareAdapter} from '@homeaway/redux-middleware-adapter';

// Relative Imports
import Reducers from './reducers/all';
import registerAllMiddleware from './middleware/all';
import limiterMiddleware from './middleware/utils/limiterMiddleware';
import {determineDevice, determineDeviceSize, getInitialSize} from './middleware/windowHandler';
import CarouselRecord from './records/carouselRecord';
import MapRecord from './records/mapRecord';
import ListingPageRecord from './records/listingPageRecord';
import ListingExtendedRecord from './records/listingExtendedRecord';
import ReviewsRecord from './records/reviewsRecord';
import {SystemStateRecord, Devices} from './records/systemStateRecord';
import TripRecord from './records/tripRecord';
import GuestSelectorRecord from './records/guestSelectorRecord';
import DatePickerRecord from './records/datePickerRecord';
import TravelerFeaturesRecord from './records/travelerFeaturesRecord';
import ContactOwnerFormRecord from './records/contactOwnerFormRecord';
import NavigationRecord from './records/navigationRecord';
import {readLocalStorage} from './utils/localStorage';
import {FormattedDateUtils} from './utils/dateHelper';
import {readCookie} from './utils/cookieHelpers';
import formatAffiliateCookie from './utils/formatAffiliateCookie';
import {getQueryParams} from './utils/queryStringHelper';

// Primary createStore function that is used by the other two functions below.
function createStore(initialState = {}, router = {}) {
    // Each middleware function is registered with the middlewareAdapter and
    // is assigned an action to respond to. See './middleware/all'
    registerAllMiddleware();

    let composeEnhancers = compose;

    // Setup redux devtools chrome extension, if available and correct env
    if (
        typeof window !== 'undefined' &&
        (initialState.systemReducer.environment === 'development' ||
            initialState.systemReducer.devTools)
    ) {
        composeEnhancers = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose;
    }

    const createStoreWithMiddleware = composeEnhancers(
        router.enhancer,
        applyMiddleware(middlewareAdapter, limiterMiddleware, router.middleware)
    )(reduxCreateStore);

    const reducers = combineReducers(Object.assign({}, {router: router.reducer}, Reducers));

    return createStoreWithMiddleware(reducers, initialState);
}

// During server-side render, our API layer returns data. We take the raw
// object and assign parts of that data to reducers, then build the store.
function createStoreFromData(obj, system, router) {
    const {
        listing,
        reviews,
        reviewsSummary,
        ownerRating,
        header,
        translations,
        travelerFeatures
    } = obj;

    const reviewsData = Object.assign({}, reviews, reviewsSummary, {ownerRating});

    return createStore(
        {
            listingPageReducer: new ListingPageRecord(),
            listingExtendedReducer: new ListingExtendedRecord(),
            carouselReducer: new CarouselRecord(),
            mapReducer: new MapRecord(),
            listingReducer: listing,
            systemReducer: new SystemStateRecord(system),
            translationsReducer: translations,
            reviewsReducer: new ReviewsRecord(reviewsData),
            headerReducer: {...header},
            navigationReducer: new NavigationRecord(),
            guestSelectorReducer: new GuestSelectorRecord(),
            datePickerReducer: new DatePickerRecord(),
            tripDetailsReducer: new TripRecord(),
            travelerFeaturesReducer: new TravelerFeaturesRecord({...travelerFeatures}),
            contactOwnerFormReducer: new ContactOwnerFormRecord(), // populated via local storage on client,
            abacusTestsReducer: obj.testBuckets || {},
            seoLinkpile: obj.seoLinkpile
        },
        router
    );
}

function createSystemRecord(state, requestState) {
    const queryParams = getQueryParams(window.location.search) || {};
    const cid = queryParams.cid;
    const cookies = {
        'ha-state-prst': readCookie('ha-state-prst', window.document.cookie),
        'ha-trip-prst': readCookie('ha-trip-prst', window.document.cookie),
        ha_affiliate_broker: cid ? formatAffiliateCookie(cid) : null
    };
    const systemReducer = Object.assign({}, state.systemReducer, {
        clientSideRendered: true,
        stickyMap: new Map(state.systemReducer.stickyMap || {}),
        queryParamsOnEntry: queryParams,
        userAgent: window.navigator.userAgent,
        requestMarker: requestState.requestMarkerShortForm,
        cookies
    });
    const initialSystemState = new SystemStateRecord(systemReducer);

    const {height, width} = getInitialSize();
    // initialSystemState (isMobile/Tablet/Desktop) is determined by user agent,
    // and it drives the SSR.
    // When hydrating on the client side, we want to clamp it to mobile if it
    // was that previously, due to the bundle.
    let initialDevice = initialSystemState.isMobile
        ? Devices.mobile
        : determineDevice(height, width);

    const deviceStateInformation = {
        device: initialDevice,
        clientSideRendered: true,
        deviceSize: determineDeviceSize(height, width),
        isDesktop: initialDevice === Devices.desktop,
        isMobile: initialDevice === Devices.mobile,
        isMobileUserAgent: initialSystemState.isMobile,
        isTablet: initialDevice === Devices.tablet,
        height,
        width
    };

    return initialSystemState.merge(deviceStateInformation);
}

function creatingListingPageRecord(state) {
    const listingPageReducer = Object.assign({}, state.listingPageReducer, {
        pricingKey: new Map(state.listingPageReducer.pricingKey || {})
    });

    return new ListingPageRecord(listingPageReducer);
}

// During the client-side render, we initialize our store with data that was written
// to the html page. That data was serialized so here we need to build the correct
// structure and use records where appropriate.

// If we want to initialize an attribute as an empty map/list, delete it from serialized store
// so it will rehydrate from record
/* eslint-disable complexity */
function createStoreFromSerializedStore(state, router, requestState) {
    delete state.listingPageReducer.testsExposed;

    const savedInquiryData = readLocalStorage('ha-inquiry-prst') || {};
    const localeParts = state.systemReducer.locale.split('_');
    const localeCountry = localeParts.length ? localeParts[1].toLowerCase() : 'us';
    const today = new FormattedDateUtils().today;

    // set current date based on timezone of client
    if (typeof state.tripDetailsReducer.toJS !== 'undefined') {
        state.tripDetailsReducer.set('currentDate', today);
    } else {
        state.tripDetailsReducer.currentDate = today;
    }

    state.mapReducer = new MapRecord();
    state.carouselReducer = new CarouselRecord(state.carouselReducer || {});
    state.systemReducer = createSystemRecord(state, requestState);
    state.listingPageReducer = creatingListingPageRecord(state);
    state.listingExtendedReducer = new ListingExtendedRecord({});
    state.reviewsReducer.reviews = state.reviewsReducer.reviews || {};
    state.reviewsReducer = new ReviewsRecord(state.reviewsReducer || {});
    state.tripDetailsReducer = new TripRecord(state.tripDetailsReducer || {});
    state.guestSelectorReducer = new GuestSelectorRecord({});
    state.datePickerReducer = new DatePickerRecord({});
    state.navigationReducer = new NavigationRecord({});
    state.travelerFeaturesReducer = new TravelerFeaturesRecord(state.travelerFeaturesReducer || {});
    state.contactOwnerFormReducer = new ContactOwnerFormRecord({
        firstName: savedInquiryData.firstName,
        lastName: savedInquiryData.lastName,
        phoneNumber: savedInquiryData.phoneNumber,
        emailAddress: savedInquiryData.emailAddress,
        countryCode: savedInquiryData.countryCode,
        countryName: savedInquiryData.countryName || localeCountry,
        termsAndConditionsAgreed: !state.travelerFeaturesReducer.termsAndConditionsRequired,
        newsSubscribed: state.travelerFeaturesReducer.showNewsCheckbox
            ? savedInquiryData.newsSubscribed
            : true
    });

    return createStore(state, router);
}
/* eslint-enable complexity */

export {createStoreFromData, createStoreFromSerializedStore};
